import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';
import { Option } from '@webapp/types/common/option';

import Box from '@components/layout/box';
import Hint from '@components/basics/hint';
import Flex from '@components/layout/flex';
import Helper from '@components/basics/helper';
import InputField from '@components/form/input';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import { GenericDropdownBuilder } from '@webapp/controllers/form/dropdown';

import LanguageCtrl from '../../language';
import { PresetReleaseTimeCtrl } from '../preset-release-time';
import GenresSelectorCtrl from '@webapp/controllers/presets/genres-selector';

import ArtistPreset from '@common/types/artist-preset/artist-preset';
import { CatalogTier } from '@common/types/artist-preset/catalog-tier';

import {
  UpdateArtistPresets,
  UpdateArtistPresetsField,
} from '@webapp/store/artist-presets/action-creators';
import { UPDATE_ARTIST_PRESETS } from '@webapp/store/artist-presets/actions';
import { useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

//utils
import { isRecordLabelNameValid } from '@webapp/utils/record-labels/is-record-label-name-valid';

interface PresetsListCtrlProps {
  className?: string;

  presets?: ArtistPreset;
  isLoading: boolean;

  updatePresets: (presets: ArtistPreset) => UpdateArtistPresets;
  updatePresetsField: <T extends keyof ArtistPreset>(
    field: T,
    value: ArtistPreset[T]
  ) => UpdateArtistPresetsField<T>;
}

const SINGLE_PRICES: Option<CatalogTier>[] = [
  { label: '$0.69', value: 'BACK' },
  { label: '$0.99', value: 'MID' },
  { label: '$1.29', value: 'FRONT' },
];

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: !!state.ui.loading[UPDATE_ARTIST_PRESETS],
    presets: state.presets.presets,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updatePresets: (presets: ArtistPreset) =>
    dispatch(new UpdateArtistPresets(presets)),
  updatePresetsField: <T extends keyof ArtistPreset>(
    field: T,
    value: ArtistPreset[T]
  ) => dispatch(new UpdateArtistPresetsField<T>(field, value)),
});

const PriceDropdownCtrl = GenericDropdownBuilder<CatalogTier>();

export const PresetsListCtrl: React.FC<PresetsListCtrlProps> = (props) => {
  const [hasPriceHelper, setHasPriceHelper] = React.useState<boolean>(false);
  const [hasReleaseTimeHelper, setHasReleaseTimeHelper] =
    React.useState<boolean>(false);
  const [isMounted, setIsMounted] = React.useState<boolean>(false);
  const [recordLabelName, setRecordLabelName] = React.useState<string>('');
  const [recordLabelErrmsg, setRecordLabelErrmsg] = React.useState<string>('');

  const savePresets = useDebouncedCallback(async (presets) => {
    props.updatePresets(presets);
  }, 2000);

  useEffect(() => {
    if (isMounted && props.presets) {
      savePresets(props.presets);
    }
  }, [props.presets, isMounted, savePresets]);

  useEffect(() => {
    setIsMounted(true);
    setRecordLabelName(props.presets?.recordLabel || '');
  }, [props.presets]);

  const updateField =
    <T extends keyof ArtistPreset>(field: T) =>
    (value: ArtistPreset[T]) => {
      props.updatePresetsField<T>(field, value);
    };

  const setRecordLabel = (recordLabel: string) => {
    setRecordLabelName(recordLabel);
    if (!isRecordLabelNameValid(recordLabel)) {
      setRecordLabelErrmsg('LABEL.NAME.ERROR_MESSAGE');
      return;
    }
    props.updatePresetsField<'recordLabel'>('recordLabel', recordLabel);
  };

  return (
    <MotionOpacity className={props.className}>
      <Box>
        {/* Copyright */}
        <Typography className="mb-20" type="section-title" customFont="medium">
          <Translate id="ARTIST_PROFILE.PRESETS.COPYWRITE_INFORMATION" />
        </Typography>
        <InputField
          className="mb-40"
          label="Copyright"
          value={props.presets?.copyright || ''}
          onChange={updateField<'copyright'>('copyright')}
        />

        {/* Price per Single */}
        <Flex
          className="single-wrapper mb-20"
          justifyContent="flex-start"
          alignItems="center"
          gap={5}
        >
          <Typography type="section-title" customFont="medium">
            <Translate id="ARTIST_PROFILE.PRESETS.PRICE_PER_SINGLE" />
          </Typography>
          <QuestionMark onClick={() => setHasPriceHelper(!hasPriceHelper)}>
            <Typography weight="700">?</Typography>
          </QuestionMark>
          <Helper textLeft isHover={hasPriceHelper}>
            Set a price for your listeners who wish to buy your single. Only
            available on iTunes.
          </Helper>
        </Flex>
        <PriceDropdownCtrl
          className="mb-40"
          title={
            <Translate id="ARTIST_PROFILE.PRESETS.PRICE_PER_SINGLE.LABEL" />
          }
          value={props.presets?.pricePerSingle}
          options={SINGLE_PRICES}
          onChange={updateField<'pricePerSingle'>('pricePerSingle')}
        />

        {/* Release Time */}
        <WrapperTime className="mb-20">
          <Flex justifyContent="flex-start" alignItems="center" gap={5}>
            <Typography type="section-title" customFont="medium">
              <Translate id="ARTIST_PROFILE.PRESETS.RELEASE_TIME" />
            </Typography>
            <QuestionMark
              onClick={() => setHasReleaseTimeHelper(!hasReleaseTimeHelper)}
            >
              <Typography weight="700">?</Typography>
            </QuestionMark>
            <Helper textLeft isHover={hasReleaseTimeHelper}>
              Set a price for your listeners who wish to buy your single. Only
              available on iTunes.
            </Helper>
          </Flex>
          <Typography type="small" customFont="light" opacity="0.5">
            <Translate id="ARTIST_PROFILE.PRESETS.RELEASE_TIME.UTC" />
          </Typography>
        </WrapperTime>

        <PresetReleaseTimeCtrl
          className="mb-40"
          time={props.presets?.releaseTime}
          onChange={updateField<'releaseTime'>('releaseTime')}
        />

        {/* Record Label */}
        <Typography className="mb-20" type="section-title" customFont="medium">
          <Translate id="ARTIST_PROFILE.PRESETS.RECORD_LABEL" />
        </Typography>
        <InputField
          className="mb-40"
          label="Record Label"
          value={recordLabelName}
          error={
            recordLabelErrmsg !== '' ? <Translate id={recordLabelErrmsg} /> : ''
          }
          onChange={(value) => {
            setRecordLabelErrmsg('');
            setRecordLabel(value);
          }}
        />
        <Hint
          className="mb-40"
          noBorderTop
          customTitle={<Translate id="ARTIST_PROFILE.PRESETS.HINT.TITLE" />}
        >
          <Translate id="ARTIST_PROFILE.PRESETS.HINT.DESCRIPTION" />
        </Hint>

        {/* Genres */}
        <GenresSelectorCtrl className="mb-40" section="presets" />

        {/* Language */}
        <Typography className="mb-20" type="section-title" customFont="medium">
          <Translate id="ARTIST_PROFILE.PRESETS.LANGUAGE" />
        </Typography>
        <LanguageCtrl
          value={props.presets?.language}
          onChange={updateField<'language'>('language')}
        />
      </Box>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(PresetsListCtrl)`
  .single-wrapper {
    position: relative;
  }
`);

const WrapperTime = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const QuestionMark = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  height: 16px;
  width: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: help;
  span {
    font-size: ${({ theme }) => theme.typography.small};
    color: ${({ theme }) => theme.colors.background};
  }
`;
